import { useRouter } from 'next/router'
import NextLink from 'next/link'
import NextImage from 'next/image'
import React from 'react'

import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { historyUtils } from '@wartek-id/toolbox-helper'
import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
  HeaderBarRightAction,
} from '@wartek-id/header-bar'
import { Text } from '@wartek-id/text-v1'

import { CategoryListItem } from '../components/CategoryListItem/CategoryListItem'
import { DocumentListItem } from '../components/CategoryListItem/DocumentListItem'
import DocumentEmptyState from 'app/DocumentCollectionPage/components/DocumentEmptyState'
import DesktopPageHeader from 'components/DesktopPageHeader'
import { DesktopCategoryLayout } from '../components/DesktopCategoryLayout'
import { DesktopSplitViewLayout } from '../components/DesktopSplitViewLayout'
import { useSETrackEvent } from '../hooks/useSETrackEvent'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useDocumentDesktopView } from '../hooks/useDocumentDesktopView'
import {
  CATEGORY_TYPE_ENUM,
  useAPIDocumentCollectionCategoryDetails,
  type ResponseSuccessGetDocumentCategoryDetail,
} from 'api/document_collection/useAPIDocumentCollection'
import { HELPDESK_LINK } from 'app/DocumentCollectionPage/constants'
import KMDisclaimer from '../components/KMDisclaimer'

import type { TContentSearchResult } from 'types/content-search'

type DocumentCategoryCollectionPageProps = {
  parentId?: string | string[]
  id?: string | string[]
  isLoading?: boolean
  categoryInfo: ResponseSuccessGetDocumentCategoryDetail['data']
}

interface ILinkProps {
  isExternalLink?: boolean
  href: string
  children: React.ReactNode
  target: string
  onClick?: () => void
}

const LoadingState = (props) => {
  const { length = 10 } = props

  return (
    <>
      {Array.from({ length })?.map((_, index) => (
        <Shimmer
          key={index}
          height={80}
          className="mt-4 !w-full"
          style={{
            borderRadius: 12,
          }}
        />
      ))}
    </>
  )
}

export const DocumentCategoryCollectionPage = (
  props: DocumentCategoryCollectionPageProps
) => {
  const { id, parentId, isLoading = false, categoryInfo } = props

  const router = useRouter()
  const { SETrackDocumentLandingPage } = useSETrackEvent()
  const [, setValue, , getValue] = useLocalStorage('categoryLvl', null)
  const { isDesktop } = useDocumentDesktopView()

  const isCategory = !id || !!parentId
  const isDocumentList = !!id
  const categoryLvl = getValue()

  // Fetch parent category to build deeper breadcrumb ancestry
  const { data: parentCategoryData } = useAPIDocumentCollectionCategoryDetails(
    categoryInfo?.parentId ?? null
  )
  const parentCategoryInfo = parentCategoryData?.data

  const [selectedDocumentId, setSelectedDocumentId] = React.useState<
    string | null
  >(null)

  // Reset selection when id changes
  React.useEffect(() => {
    setSelectedDocumentId(null)
  }, [id])

  const Link = ({
    isExternalLink = false,
    href,
    children,
    target,
    onClick,
  }: ILinkProps) => {
    return isExternalLink ? (
      <a href={href} target={target} onClick={onClick}>
        {children}
      </a>
    ) : (
      <NextLink href={href} passHref target={target}>
        <a onClick={onClick}>{children}</a>
      </NextLink>
    )
  }

  const setHelpTracker = () => {
    let attributes = {}
    const activityType = 'CLICK_HELP_KUMPULAN_DOKUMEN'

    if (categoryInfo.categoryType === CATEGORY_TYPE_ENUM.CT_MAIN_CATEGORY) {
      attributes = {
        screen_name: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
        event_trigger: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
        category: categoryInfo?.name,
        url: HELPDESK_LINK,
      }
    } else if (
      categoryInfo?.categoryType === CATEGORY_TYPE_ENUM.CT_SUB_CATEGORY ||
      categoryInfo?.categoryType === CATEGORY_TYPE_ENUM.CT_UNDEFINED
    ) {
      attributes = {
        screen_name: 'KUMPULAN_DOKUMEN_SUBCATEGORY_PAGE',
        event_trigger: 'KUMPULAN_DOKUMEN_SUBCATEGORY_PAGE',
        parent_category: categoryInfo?.parentName,
        category: categoryInfo?.name,
        category_level: categoryLvl,
        url: HELPDESK_LINK,
      }
    } else if (
      categoryInfo?.categoryType === CATEGORY_TYPE_ENUM.CT_END_CATEGORY
    ) {
      attributes = {
        screen_name: 'KUMPULAN_DOKUMEN_DOCUMENT_LIST_PAGE',
        event_trigger: 'KUMPULAN_DOKUMEN_DOCUMENT_LIST_PAGE',
        parent_category: categoryInfo?.parentName,
        category: categoryInfo?.name,
        category_level: categoryLvl,
        url: HELPDESK_LINK,
      }
    }

    SETrackDocumentLandingPage(activityType, attributes)
  }

  // Category info section — shared between split view and regular layout
  const CategoryInfoSection = () => (
    <div className="flex flex-row p-4 space-x-3">
      <div
        className="flex flex-col space-y-2 grow"
        style={{ wordBreak: 'break-word' }}
      >
        {Boolean(categoryInfo?.parentName) && (
          <Text
            variant="subheading"
            color="subdued"
            className="uppercase line-clamp-1"
          >
            {categoryInfo?.parentName}
          </Text>
        )}
        <Text variant="heading-lg" className="line-clamp-2">
          {categoryInfo?.name}
        </Text>
        <Text className="line-clamp-2" variant="helper">
          {categoryInfo?.description}
        </Text>
      </div>
      {categoryInfo?.thumbnailUrl && (
        <div className="flex-none w-[84px] h-[84px]">
          <NextImage
            src={categoryInfo?.thumbnailUrl}
            alt="Documents"
            width="84px"
            height="84px"
            className="object-cover object-center"
            layout="responsive"
          />
        </div>
      )}
    </div>
  )

  const headerBar = (
    <div className="fixed top-0 inset-x-0 z-50 bg-white">
      <div className="max-w-xl m-auto">
        <HeaderBar borderless>
          <HeaderBarLeftAction
            onClick={() => {
              setValue(categoryLvl - 1)
              historyUtils?.backToApp(router?.back)
            }}
          />
          <HeaderBarTitle className="line-clamp-1">
            Dokumen Rujukan
          </HeaderBarTitle>

          <HeaderBarRightAction>
            <Link
              href={HELPDESK_LINK}
              isExternalLink={true}
              target="_blank"
              onClick={() => setHelpTracker()}
            >
              <Text
                variant="heading-sm"
                color="informational"
                className="!text-blue-60"
              >
                Bantuan
              </Text>
            </Link>
          </HeaderBarRightAction>
        </HeaderBar>
      </div>
    </div>
  )

  // Shared breadcrumb items for all desktop pages — up to 4 levels deep
  const breadcrumbItems = [
    { label: 'Dokumen Rujukan', href: '/dokumen' },
    // Grandparent: parent's parent
    ...(parentCategoryInfo?.parentName && parentCategoryInfo?.parentId
      ? [
          {
            label: parentCategoryInfo.parentName,
            href: `/dokumen/kategori/${parentCategoryInfo.parentId}`,
          },
        ]
      : []),
    // Parent
    ...(categoryInfo?.parentName && categoryInfo?.parentId
      ? [
          {
            label: categoryInfo.parentName,
            href: `/dokumen/kategori/${categoryInfo.parentId}`,
          },
        ]
      : []),
    // Current
    ...(categoryInfo?.name ? [{ label: categoryInfo.name, href: null }] : []),
  ]

  const desktopHeader = (
    <DesktopPageHeader
      breadcrumbItems={breadcrumbItems}
      navLinks={[{ label: 'Bantuan', href: HELPDESK_LINK, external: true }]}
    />
  )

  // Desktop category page
  if (isCategory && isDesktop) {
    return (
      <DesktopCategoryLayout
        isLoading={isLoading}
        categoryInfo={categoryInfo}
        desktopHeader={desktopHeader}
        Link={Link}
        onGuideClick={() =>
          SETrackDocumentLandingPage('CLICK_KUMPULAN_DOKUMEN_PENGANTAR', {
            screen_name: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
            event_trigger: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
            category: categoryInfo?.name,
            url: categoryInfo?.guideUrl,
          })
        }
      />
    )
  }

  // Desktop split view for document list page
  if (isDocumentList && isDesktop) {
    return (
      <DesktopSplitViewLayout
        isLoading={isLoading}
        categoryInfo={categoryInfo}
        desktopHeader={desktopHeader}
        selectedDocumentId={selectedDocumentId}
        onSelectDocument={(doc: TContentSearchResult) =>
          setSelectedDocumentId(doc?.collectionId)
        }
        onGuideClick={() =>
          SETrackDocumentLandingPage('CLICK_KUMPULAN_DOKUMEN_PENGANTAR', {
            screen_name: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
            event_trigger: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
            category: categoryInfo?.name,
            url: categoryInfo?.guideUrl,
          })
        }
        Link={Link}
        CategoryInfoSection={CategoryInfoSection}
      />
    )
  }

  // Mobile + category list: original layout
  return (
    <div className="max-w-xl m-auto bg-surface-subdued min-h-[95vh]">
      {headerBar}

      <div className="pt-14">
        <main className="bg-white">
          {isLoading ? (
            <div className="flex flex-col p-4">
              <div className="flex flex-row items-center justify-between">
                <div className="mt-2 flex flex-col !w-full">
                  <Shimmer
                    height={15}
                    className="mb-4 !w-full"
                    style={{
                      borderRadius: 12,
                    }}
                  />

                  <Shimmer
                    height={10}
                    className="mb-4 !w-full"
                    style={{
                      borderRadius: 12,
                    }}
                  />

                  <Shimmer
                    height={10}
                    className="mb-4 !w-full"
                    style={{
                      borderRadius: 12,
                    }}
                  />
                </div>

                <Shimmer
                  height={80}
                  className="ml-4 !w-full max-w-[25vw]"
                  style={{
                    borderRadius: 12,
                  }}
                />
              </div>

              <LoadingState length={1} />
            </div>
          ) : (
            <>
              <div className="flex flex-row p-4 space-x-3">
                <div
                  className="flex flex-col space-y-2 grow"
                  style={{ wordBreak: 'break-word' }}
                >
                  {Boolean(categoryInfo?.parentName) && (
                    <Text
                      variant="subheading"
                      color="subdued"
                      className="uppercase line-clamp-1"
                    >
                      {categoryInfo?.parentName}
                    </Text>
                  )}
                  <Text variant="heading-lg" className="line-clamp-2">
                    {categoryInfo?.name}
                  </Text>
                  <Text className="line-clamp-2" variant="helper">
                    {categoryInfo?.description}
                  </Text>
                </div>
                {categoryInfo?.thumbnailUrl && (
                  <div className="flex-none w-[84px] h-[84px]">
                    <NextImage
                      src={categoryInfo?.thumbnailUrl}
                      alt="Documents"
                      width="84px"
                      height="84px"
                      className="object-cover object-center"
                      layout="responsive"
                    />
                  </div>
                )}
              </div>

              {categoryInfo?.guideUrl && (
                <div className="px-4 md:px-8 mb-4">
                  <Link
                    href={categoryInfo?.guideUrl}
                    isExternalLink={true}
                    target="_blank"
                    onClick={() =>
                      SETrackDocumentLandingPage(
                        'CLICK_KUMPULAN_DOKUMEN_PENGANTAR',
                        {
                          screen_name: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
                          event_trigger: 'KUMPULAN_DOKUMEN_CATEGORY_PAGE',
                          category: categoryInfo?.name,
                          url: categoryInfo?.guideUrl,
                        }
                      )
                    }
                  >
                    <div className="flex flex-row justify-between p-4 mt-4 space-x-4 border rounded-lg">
                      <div className="flex-none w-8 h-8">
                        <NextImage
                          src={categoryInfo?.guideThumbnailUrl}
                          alt="Guide"
                          width="32px"
                          height="32px"
                          className="object-cover object-center"
                          layout="responsive"
                        />
                      </div>

                      <div className="grow flex flex-col ml-4 space-y-[2px]">
                        <Text variant="action" className="line-clamp-3">
                          {categoryInfo?.guideTitle}
                        </Text>

                        <Text
                          variant="body-sm"
                          className="line-clamp-3"
                          color="subdued"
                        >
                          {categoryInfo?.guideContent}
                        </Text>
                      </div>

                      <div className="flex-none">
                        <Icon className="!text-subdued">chevron_right</Icon>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
              {categoryInfo?.categoryType !== 'CT_UNDEFINED' && (
                <KMDisclaimer className="mb-3 px-4 md:px-8" />
              )}
            </>
          )}

          <div className="h-full py-2 bg-surface-subdued">
            {categoryInfo?.categoryType === 'CT_UNDEFINED' ? (
              <DocumentEmptyState />
            ) : (
              <>
                {isCategory && (
                  <section className="px-4 md:px-8">
                    <CategoryListItem parentData={categoryInfo} />
                  </section>
                )}
                {/* TODO: adjust ui in DocumentListItem */}
                {isDocumentList && (
                  <DocumentListItem parentData={categoryInfo} />
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
